/* eslint-disable no-use-before-define */
/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react'
import {
  useParams,
  useNavigate,
  matchRoutes,
  useLocation,
} from 'react-router-dom'
import {
  Button,
  IconButton,
  Typography,
  TextField,
  Grid,
  Box,
  Switch,
  MenuItem,
  CircularProgress,
  Grow,
  FormControlLabel,
  InputAdornment,
  Alert,
} from '@mui/material'
import {
  useForm,
  Controller,
  SubmitHandler,
  Control,
  useWatch,
  UseFormGetValues,
  UseFormResetField,
  Path,
} from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { EstateAgentUserDTO } from 'api/services/Agent/types'
import { agentTemplate } from './constants'
import { set } from 'lodash'
import {
  PASSWORD_REGEX,
  SOURCES,
  EMAIL_REGEX,
  SOURCE_OPTIONS,
} from 'helpers/constants'
import {
  Visibility,
  VisibilityOff,
  Language,
  Videocam,
  Instagram,
  Facebook,
  LocalPhone,
  PhoneAndroid,
} from '@mui/icons-material'
import {
  Attachment,
  ContactInfo,
  I18nAddress,
  I18nText,
  SocialMediaInfo,
  User,
  FilePathWithPreview,
  AttachmentType,
  Source,
} from 'types'
import AgentService from 'api/services/Agent'
import Images from './Images'
import Partners from './Partners'
import KeycloakService from 'services/KeycloakService'

import NotFound from 'components/NotFound'
import { useAuthStore } from 'store'
import RenderOnAdmin from 'components/access/RenderOnAdmin'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import usePageView from 'hooks/usePageView'
import { AgentStatusEnum } from 'types/enums'

interface ExternalProps {
  control: Control<AgentCreateFormFields>
  getValues: UseFormGetValues<AgentCreateFormFields>
  resetField: UseFormResetField<AgentCreateFormFields>
  isInEditMode: boolean
  isActiveUser: boolean
}

function External(props: ExternalProps) {
  const { t } = useTranslation()

  const { control, getValues, resetField, isInEditMode, isActiveUser } = props

  const watchedSource = useWatch({
    control,
    name: 'estateAgentSystem',
  })

  const SHOULD_HIDE = [
    SOURCES.REAL_STATUS as typeof watchedSource,
    SOURCES.GOLDEN_HOME as typeof watchedSource,
    SOURCES.ESTATE_PLUS as typeof watchedSource,
    SOURCES.ILIST as typeof watchedSource,
    SOURCES.ZOIA as typeof watchedSource,
  ].includes(watchedSource)

  React.useEffect(() => {
    if (
      !watchedSource ||
      ![
        SOURCES.ORBIT as typeof watchedSource,
        SOURCES.FORTUNET as typeof watchedSource,
      ].includes(watchedSource)
    ) {
      if (!isInEditMode) {
        resetField('externalId', { defaultValue: '' })
      }
    }
  }, [isInEditMode, resetField, watchedSource])

  if (SOURCES.NONE === watchedSource && !KeycloakService.isAdmin()) {
    return null
  }

  return (
    <>
      {[
        SOURCES.ORBIT as typeof watchedSource,
        SOURCES.FORTUNET as typeof watchedSource,
      ].includes(watchedSource) ||
      (isInEditMode && !SHOULD_HIDE) ? (
        <Grid item xs={12} sm={6}>
          <Controller
            name="externalId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                fullWidth
                disabled={
                  isInEditMode && (!KeycloakService.isAdmin() || isActiveUser)
                }
                sx={{
                  display:
                    watchedSource === SOURCES.NONE ||
                    watchedSource === SOURCES.REAL_STATUS
                      ? 'none'
                      : undefined,
                }}
                variant="outlined"
                label={`${t(
                  'dashboard.pages.createAgent.company.fields.crmCode'
                )}${
                  (isInEditMode && !KeycloakService.isAdmin()) ||
                  SOURCES.NONE === watchedSource ||
                  SOURCES.REAL_STATUS === watchedSource
                    ? ''
                    : '*'
                }`}
                size="small"
                {...field}
                error={!!error}
                helperText={error ? error?.message : ' '}
              />
            )}
            rules={{
              validate: () => {
                if (
                  SOURCES.NONE === watchedSource &&
                  KeycloakService.isAdmin()
                ) {
                  return true
                }
                if (
                  (getValues('estateAgentSystem') !== SOURCES.ORBIT ||
                    getValues('estateAgentSystem') !== SOURCES.FORTUNET) &&
                  getValues('externalId') === '' &&
                  !SHOULD_HIDE
                )
                  return false
              },
            }}
          />
        </Grid>
      ) : null}
    </>
  )
}

export interface AgentCreateFormFields {
  // active: boolean
  entityStatus: AgentStatusEnum
  user: User
  address: I18nAddress
  partners: ContactInfo[]
  companyName: I18nText
  companyTitle: I18nText
  description: string
  comments: string
  externalId: string
  estateAgentSystem: Source | ''
  companyCommercialId: string
  vatNumber: string
  contactInfo: ContactInfo
  contactImage: FilePathWithPreview | null
  socialMedia: {
    Website: SocialMediaInfo
    Facebook: SocialMediaInfo
    Instagram: SocialMediaInfo
    Video: SocialMediaInfo
  }
  attachments: {
    logo: FilePathWithPreview | null
    banner: FilePathWithPreview | null
  }
}

interface AgentInfo {
  id: number | string
  alternativeId: number | string
  externalId: number | string
  dateTimeCreated: any
  dateTimeUpdated: any
  entityStatus: any
  entityOwner: any
  systemName: string
  systemSourceCode: string
  agentExternalIdType: string
  active: boolean
  useGenericApi: boolean
}

const DefaultValues = {
  // active: false,
  entityStatus: AgentStatusEnum.Inactive,
  user: {
    username: '',
    password: '',
    person: {
      firstName: '',
      lastName: '',
    },
  },
  companyName: {
    defaultValue: null,
    id: null,
    locale: 'el',
    text: '',
  },
  companyTitle: {
    defaultValue: null,
    id: null,
    locale: 'el',
    text: '',
  },
  vatNumber: '',
  companyCommercialId: '',
  source: '' as Source,
  externalId: '',
  comments: '',
  description: '',
  address: {
    addressDescription: '',
    area: '',
    city: '',
    country: 'Ελλάδα',
    id: null,
    postalCode: '',
    street: '',
    streetNumber: '',
    locale: 'el',
  },
  contactInfo: {
    contactName: '',
    landLineNumber: '',
    mobileNumber: '',
    extraNumber: '',
    id: null,
    defaultContactInfo: true,
    defaultValue: null,
    email: '',
    description: '',
    locale: 'el',
    attachments: [],
    location: {
      addresses: [],
      id: null,
    },
  },
  contactImage: null,
  partners: [],

  attachments: {
    logo: null,
    banner: null,
  },
  socialMedia: {
    Website: {
      id: null,
      mediaTitle: 'Website',
      url: '',
    },
    Facebook: {
      id: null,
      mediaTitle: 'Facebook',
      url: '',
    },
    Instagram: {
      id: null,
      mediaTitle: 'Instagram',
      url: '',
    },
    Video: {
      id: null,
      mediaTitle: 'Video',
      url: '',
    },
  },
}

export default function CreateEditAgent() {
  const { sendPageView } = usePageView({ runOnMount: false })
  const location = useLocation()

  const { executeRecaptcha } = useGoogleReCaptcha()

  const createGoogleToken = React.useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available')
      return
    }
    return await executeRecaptcha()
  }, [executeRecaptcha])

  const queryClient = useQueryClient()

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
  }, [location])

  const routes = [
    { path: '/agents/create' },
    { path: '/agents/:agentId' },
    { path: '/profile' },
  ]

  const matchedRoutes = matchRoutes(routes, location)

  const matchEdit =
    !!matchedRoutes?.length &&
    !!matchedRoutes.find(
      (matchedRoute) => matchedRoute?.route?.path === '/agents/:agentId'
    )

  const matchCreate =
    !!matchedRoutes?.length &&
    !!matchedRoutes.find(
      (matchedRoute) => matchedRoute?.route?.path === '/agents/create'
    )

  const matchProfile =
    !!matchedRoutes?.length &&
    !!matchedRoutes.find(
      (matchedRoute) => matchedRoute?.route?.path === '/profile'
    )

  React.useEffect(() => {
    if (matchCreate) {
      sendPageView()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { agentId } = useParams()

  const currentUser = useAuthStore((state) => state.user)

  const [localError, setLocalError] = React.useState<boolean>(false)

  const handleCloseError = () => {
    setLocalError(false)
  }

  const [notFound, setNotFound] = React.useState<boolean>(false)

  const [localAgent, setLocalAgent] = React.useState<
    EstateAgentUserDTO | undefined
  >()

  const { t } = useTranslation()

  const navigate = useNavigate()

  const [showPassword, setShowPassword] = React.useState(false)

  const handleShowPassword = () => {
    setShowPassword((old) => !old)
  }

  const handleMouseDownPassword = (event) => {
    event.preventDefault()
  }

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    watch,
    resetField,
  } = useForm<AgentCreateFormFields>({
    defaultValues: DefaultValues,
  })

  const watchedUserStatus = watch('entityStatus')

  const { enqueueSnackbar } = useSnackbar()

  const initEstateAgent = (data: EstateAgentUserDTO) => {
    if (data?.estateAgent?.cInfos?.length) {
      const prepareCompanyName = () => {
        if (data.estateAgent?.companyName) {
          const found = data.estateAgent?.companyName.find(
            (item) => item.locale === 'el'
          )
          if (found) {
            return found
          }
        }
        return DefaultValues.companyName
      }
      const prepareCompanyTitle = () => {
        if (data.estateAgent?.companyTitle) {
          const found = data?.estateAgent?.companyTitle?.find(
            (item) => item.locale === 'el'
          )
          if (found) {
            return found
          }
        }
        return DefaultValues.companyTitle
      }
      const preparePartners = () => {
        if (data.estateAgent?.cInfos?.length) {
          return data.estateAgent?.cInfos
            .filter((contact) => contact?.defaultContactInfo === false)
            .map((contact) => {
              if (contact?.attachments?.length) {
                return {
                  ...contact,
                  attachments: contact.attachments.map((attachment) => ({
                    ...attachment,
                    type: 'attachment',
                  })),
                }
              }
              return contact
            })
        }
        return []
      }

      let cInfoIdx = data.estateAgent.cInfos.findIndex(
        (item) => item?.defaultContactInfo === true
      )

      if (cInfoIdx === -1) cInfoIdx = 0

      const prepareUser = () => {
        return {
          id: data.user.id ?? null,
          username: data.user.username ?? DefaultValues.user.username,
          password: DefaultValues.user.password,
          person: {
            id: data.user?.person?.id ?? null,
            firstName:
              data.user?.person?.firstName ??
              DefaultValues.user?.person?.firstName,
            lastName:
              data.user?.person?.lastName ??
              DefaultValues.user?.person?.lastName,
          },
        }
      }

      const initContactInfo = () => {
        if (data.estateAgent.cInfos[cInfoIdx]) {
          return {
            ...data.estateAgent?.cInfos[cInfoIdx],
          }
        } else {
          return DefaultValues.contactInfo
        }
      }

      const prepareLogo = () => {
        if (data.estateAgent?.attachments?.length) {
          const found = data.estateAgent?.attachments.find(
            (item) => item.attachmentType === ('LOGO' as AttachmentType)
          )
          if (found) return { ...found, type: 'attachment' }
        }
        return null
      }

      const prepareBanner = () => {
        if (data.estateAgent?.attachments?.length) {
          const found = data.estateAgent?.attachments.find(
            (item) => item.attachmentType === ('BANNER' as AttachmentType)
          )
          if (found) return { ...found, type: 'attachment' }
        }
        return null
      }

      const prepareSocialMedia = (
        mediaTitle: 'Website' | 'Instagram' | 'Facebook' | 'Video'
      ) => {
        if (data.estateAgent?.cInfos?.[cInfoIdx]?.socialMediaInfo?.length) {
          const found = data.estateAgent?.cInfos?.[
            cInfoIdx
          ]?.socialMediaInfo?.find((item) => item.mediaTitle === mediaTitle)
          if (found) return found
        }
        return DefaultValues.socialMedia[mediaTitle]
      }

      const prepareAddressField = (
        field:
          | 'city'
          | 'area'
          | 'country'
          | 'Video'
          | 'streetNumber'
          | 'street'
          | 'postalCode',
        defaultValue = ''
      ) => {
        if (data.estateAgent?.cInfos?.[cInfoIdx]?.location?.addresses?.length) {
          const addressIdx =
            data.estateAgent.cInfos[cInfoIdx]?.location?.addresses?.findIndex(
              (item) => item?.locale === 'el'
            ) ?? -1
          if (
            addressIdx !== -1 &&
            !!data.estateAgent?.cInfos?.[cInfoIdx]?.location?.addresses?.[
              addressIdx
            ]
          )
            return (
              data.estateAgent?.cInfos?.[cInfoIdx]?.location?.addresses?.[
                addressIdx
              ]?.[field] ?? defaultValue
            )
        }
        return defaultValue
      }

      const prepareAddress = () => {
        const defaultAddress = {
          area: prepareAddressField('area'),
          city: prepareAddressField('city'),
          country: prepareAddressField('area', 'Greece'),
          postalCode: prepareAddressField('postalCode'),
          street: prepareAddressField('street'),
          streetNumber: prepareAddressField('streetNumber'),
          locale: 'el',
        }
        return defaultAddress
      }

      if (data.estateAgent?.cInfos[cInfoIdx]) {
        reset({
          partners: preparePartners(),
          entityStatus:
            data.estateAgent?.entityStatus ?? DefaultValues.entityStatus,
          externalId: data.estateAgent?.externalId ?? '',
          vatNumber: data.estateAgent?.vatNumber ?? '',
          companyCommercialId: data.estateAgent?.companyCommercialId ?? '',
          estateAgentSystem:
            (data.estateAgent?.estateAgentSystem as any)?.systemSourceCode ??
            '',
          description: data.estateAgent?.description ?? '',
          comments: data.estateAgent?.comments ?? '',
          companyName: prepareCompanyName(),
          companyTitle: prepareCompanyTitle(),
          contactInfo: {
            ...initContactInfo(),
          },
          socialMedia: {
            Website: prepareSocialMedia('Website'),
            Facebook: prepareSocialMedia('Facebook'),
            Instagram: prepareSocialMedia('Instagram'),
            Video: prepareSocialMedia('Video'),
          },
          address: prepareAddress(),
          attachments: {
            logo: prepareLogo(),
            banner: prepareBanner(),
          },
          user: prepareUser(),
        })
      }
      setLocalAgent(data)
    }
  }
  const matchProfileFallback = matchProfile
    ? currentUser?.estateAgentId
    : undefined
  const estateAgent = AgentService.hooks.useEstateAgent(
    matchEdit ? agentId : matchProfileFallback,
    {
      enabled:
        (matchEdit && !!agentId) ||
        (matchProfile && !!currentUser?.estateAgentId),
      cacheTime: 0,
      staleTime: 0,
      retry: false,
      onSettled: () => {
        sendPageView()
      },
      onSuccess: (data) => {
        initEstateAgent(data)
      },
      onError: (err) => {
        if (err?.response?.status === 404) {
          setNotFound(true)
        } else {
          setLocalError(true)
        }
        enqueueSnackbar(t('error.unspecific'), {
          variant: 'error',
        })
      },
    }
  )

  React.useEffect(() => {
    if (!!agentId && matchEdit) {
      setLocalError(false)
      estateAgent.refetch()
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
      if (localError) {
        setLocalError(false)
      }
    }
  }, [agentId])

  const IS_IN_EDIT_MODE =
    (!!agentId && estateAgent.isSuccess && estateAgent.data && matchEdit) ||
    (!!currentUser?.estateAgentId &&
      estateAgent.isSuccess &&
      estateAgent.data &&
      matchProfile)

  const createAgentMutation = AgentService.hooks.useCreateAgent({
    onMutate: () => {
      handleCloseError()
    },
    onSuccess: () => {
      enqueueSnackbar(t('signUpPage.successMessage'), {
        variant: 'success',
      })
      queryClient.invalidateQueries(AgentService.keys.QueryKeys.all)
      queryClient.invalidateQueries(AgentService.keys.QueryKeys.count)
      navigate('/agents')
    },
    onError: (error) => {
      enqueueSnackbar(t('error.unspecific'), {
        variant: 'error',
      })
    },
  })

  const updateAgentMutation = AgentService.hooks.useUpdateAgent({
    onMutate: () => {
      handleCloseError()
    },
    onSuccess: (data) => {
      enqueueSnackbar(
        t(
          `dashboard.pages.createAgent.messages.${
            IS_IN_EDIT_MODE ? 'successEdit' : 'successCreate'
          }`
        ),
        {
          variant: 'success',
        }
      )
      initEstateAgent(data)
      queryClient.invalidateQueries(AgentService.keys.QueryKeys.all)
      queryClient.invalidateQueries(AgentService.keys.QueryKeys.count)
    },
    onError: (error) => {
      enqueueSnackbar(t('error.unspecific'), {
        variant: 'error',
      })
    },
  })

  const [agentInfo, setAgentInfo] = React.useState<AgentInfo[]>([])

  const estateAgentService = AgentService.hooks.useEstateAgents({
    onSuccess: (data) => {
      setAgentInfo(data)
    },
  })
  
  const onSubmit: SubmitHandler<AgentCreateFormFields> = async (data) => {
    if (createAgentMutation.isLoading || updateAgentMutation.isLoading) return

    const payload = Object.assign(
      {},
      IS_IN_EDIT_MODE ? localAgent : agentTemplate
    ) as unknown as EstateAgentUserDTO

    const estateAgentPayload = () => {
      const matchedAgent = agentInfo?.find(
        (item) => item.systemSourceCode === data.estateAgentSystem
      )

      if (matchedAgent) {
        return {
          id: matchedAgent.id,
          systemName: matchedAgent.systemName,
          systemSourceCode: matchedAgent.systemSourceCode,
          agentExternalIdType: matchedAgent.agentExternalIdType,
          active: matchedAgent.active,
          useGenericApi: matchedAgent.useGenericApi,
        }
      }

      return null
    }

    const generateBannerLogoImages = async (): Promise<Attachment[]> => {
      const parsedPhotos: Attachment[] = []

      const imageFields = Object.entries(data.attachments)
      for (let i = 0; i < imageFields?.length; i++) {
        const file = imageFields[i][1] as unknown as FilePathWithPreview & File
        if (file != null) {
          if (file?.type === 'attachment') {
            parsedPhotos.push(file)
          } else {
            if (file) {
              const ab = await file.arrayBuffer()
              const ui8a = new Uint8Array(ab)
              const arrayData = Array.from(ui8a)

              const temp: Attachment = {
                id: null,
                alternativeId: null,
                externalId: null,
                dateTimeCreated: null,
                dateTimeUpdated: null,
                entityStatus: null,
                entityOwner: null,
                attachmentType: imageFields[
                  i
                ][0]?.toUpperCase() as AttachmentType,
                filePath: null,
                fileName: file.name,
                fileSize: file.size,
                mimeType: file.type,
                fileData: arrayData,
              }

              parsedPhotos.push(temp)
            }
          }
        }
      }
      return parsedPhotos
    }

    const generatePartnerImages = async (photos): Promise<Attachment[]> => {
      const parsedPhotos: Attachment[] = []
      for (let i = 0; i < photos?.length; i++) {
        if (photos[i]?.type === 'attachment') {
          parsedPhotos.push(photos[i])
        } else {
          const ab = await photos[i].arrayBuffer()
          const ui8a = new Uint8Array(ab)
          const byteArray = Array.from(ui8a)

          const temp: Attachment = {
            id: null,
            alternativeId: null,
            externalId: null,
            dateTimeCreated: null,
            dateTimeUpdated: null,
            entityStatus: null,
            entityOwner: null,
            attachmentType: 'PHOTO',
            filePath: null,
            fileName: photos[i].name,
            fileSize: photos[i].size,
            mimeType: photos[i].type,
            fileData: byteArray,
          }
          parsedPhotos.push(temp)
        }
      }
      return parsedPhotos
    }

    const attachments = await generateBannerLogoImages()

    set(payload, 'estateAgent.attachments', attachments)

    const generateSocialMedia = () => {
      const socialArr: SocialMediaInfo[] = []
      Object.entries(data.socialMedia).forEach((entry) => {
        const [, value] = entry
        if (value?.url?.trim()) {
          socialArr.push(value as unknown as SocialMediaInfo)
        }
      })
      return socialArr
    }

    const generateContactInfos = async (): Promise<ContactInfo[]> => {
      const defaultContact: ContactInfo = Object.assign(
        {},
        data.contactInfo
      ) as unknown as ContactInfo

      const cInfos: ContactInfo[] = []

      set(defaultContact, 'contactName', data.companyTitle?.text ?? null)
      set(defaultContact, 'socialMediaInfo', generateSocialMedia())
      set(defaultContact, 'location.addresses[0]', data.address)

      cInfos.push(defaultContact)
      for (const partner of data.partners) {
        const attachments = await generatePartnerImages(partner?.attachments)
        cInfos.push({ ...partner, attachments } as ContactInfo)
      }
      return cInfos
    }
    set(payload, 'estateAgent.cInfos', await generateContactInfos())
    set(payload, 'estateAgent.description', data.description)
    set(payload, 'estateAgent.comments', data.comments)
    set(payload, 'estateAgent.companyName[0]', data.companyName)
    set(payload, 'estateAgent.companyTitle[0]', data.companyTitle)
    set(payload, 'estateAgent.estateAgentSystem', await estateAgentPayload())
    if (KeycloakService.isAdmin()) {
      set(
        payload,
        'estateAgent.active',
        data.entityStatus === AgentStatusEnum.Active
      )
      set(payload, 'estateAgent.entityStatus', data.entityStatus)
    }
    if (IS_IN_EDIT_MODE && KeycloakService.isAdmin()) {
      if (getValues('estateAgentSystem') === SOURCES.NONE) {
        set(payload, 'estateAgent.externalId', null)
      } else {
        set(
          payload,
          'estateAgent.externalId',
          data.externalId ? data.externalId : null
        )
      }
    }
    if (
      !IS_IN_EDIT_MODE &&
      KeycloakService.isAdmin() &&
      [
        SOURCES.ORBIT as typeof data.estateAgentSystem,
        SOURCES.FORTUNET as typeof data.estateAgentSystem,
      ].includes(data.estateAgentSystem)
    ) {
      set(
        payload,
        'estateAgent.externalId',
        data.externalId ? data.externalId : null
      )
    }
    set(payload, 'estateAgent.companyCommercialId', data.companyCommercialId)
    set(payload, 'estateAgent.vatNumber', data.vatNumber)

    const generateUser = () => {
      const temp: typeof payload.user = Object.assign(
        {},
        {
          ...payload.user,
          ...data.user,
          person: {
            ...payload.user.person,
            ...data.user.person,
          },
        }
      ) as User
      if (!temp.password) {
        delete temp.password
      }

      return temp
    }

    set(payload, 'user', generateUser())

    if (IS_IN_EDIT_MODE) {
      updateAgentMutation.mutate({
        id: localAgent?.estateAgent?.id,
        payload,
      })
    } else {
      const token = await createGoogleToken()
      createAgentMutation.mutate({ ...payload, token })
    }
  }
  const matchEditFallback = matchProfile ? 'profile' : 'create'

  return (
    <>
      {estateAgent.isLoading ? (
        <CircularProgress size={40} sx={{ display: 'block', mx: 'auto' }} />
      ) : null}
      <Grow in={estateAgent.isError && notFound} unmountOnExit>
        <div>
          <NotFound />
        </div>
      </Grow>
      <Grow in={localError} unmountOnExit>
        <Alert
          severity="error"
          onClose={handleCloseError}
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          {t('error.unspecific')}
        </Alert>
      </Grow>
      <Grow in={matchCreate ? true : IS_IN_EDIT_MODE} unmountOnExit>
        <div>
          <Box
            sx={{
              bgcolor: 'background.paper',
              px: { xs: 2, sm: 2, md: 3, lg: 4, xl: 6 },
              pt: 2,
              pb: estateAgent.isLoading ? 2 : 4,
              borderRadius: 2.5,
              maxWidth: (theme) => theme.breakpoints.values.lg,
              mx: 'auto',
            }}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <Typography variant="h6" align="center">
                {t(
                  `dashboard.pages.createAgent.title.${
                    matchEdit ? 'edit' : matchEditFallback
                  }`
                )}
              </Typography>

              <Grid container rowSpacing={4} sx={{ mt: 2 }}>
                <Grid item xs={12} container spacing={2}>
                  <RenderOnAdmin>
                    <>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Controller
                              name="entityStatus"
                              control={control}
                              render={({ field }) => (
                                <Switch
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.checked
                                        ? AgentStatusEnum.Active
                                        : AgentStatusEnum.Inactive
                                    )
                                  }
                                  checked={
                                    field.value === AgentStatusEnum.Active
                                  }
                                />
                              )}
                            />
                          }
                          label={
                            t(
                              `dashboard.pages.createAgent.${
                                watchedUserStatus === AgentStatusEnum.Active
                                  ? 'active'
                                  : 'inactive'
                              }`
                            ) as unknown as string
                          }
                        />
                      </Grid>
                    </>
                  </RenderOnAdmin>
                  <Grid item xs={12}>
                    <Typography variant="body2" align="center">
                      {t('dashboard.pages.createAgent.user.labelForUser')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      rules={{
                        required: t(
                          'validation.fieldRequired'
                        ) as unknown as string,
                        pattern: {
                          value: EMAIL_REGEX,
                          message: t('validation.emailPatternError' as string),
                        },
                      }}
                      name="user.username"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          size="small"
                          disabled={matchEdit || matchProfile}
                          fullWidth
                          variant="outlined"
                          label={`${t(
                            'dashboard.pages.createAgent.user.fields.username'
                          )}*`}
                          error={!!error}
                          helperText={error ? error?.message : ' '}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      rules={{
                        required: IS_IN_EDIT_MODE
                          ? false
                          : (t(
                              'validation.fieldRequired'
                            ) as unknown as string),
                        pattern: {
                          value: PASSWORD_REGEX,
                          message: t(
                            'validation.passwordPatternError'
                          ) as unknown as string,
                        },
                      }}
                      name="user.password"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          type={showPassword ? 'text' : 'password'}
                          label={`${t(
                            'dashboard.pages.createAgent.user.fields.password'
                          )}*`}
                          error={!!error}
                          helperText={error ? error?.message : ' '}
                          inputProps={{
                            autoComplete: 'new-password',
                            form: {
                              autoComplete: 'off',
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  color={showPassword ? 'primary' : 'secondary'}
                                  onClick={handleShowPassword}
                                  onMouseDown={handleMouseDownPassword}
                                >
                                  {showPassword ? (
                                    <Visibility />
                                  ) : (
                                    <VisibilityOff />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          {...field}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`user.person.firstName`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          label={`${t(
                            'dashboard.pages.createAgent.user.fields.firstName'
                          )}`}
                          error={!!error}
                          helperText={error ? error?.message : ' '}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name={`user.person.lastName`}
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          label={`${t(
                            'dashboard.pages.createAgent.user.fields.lastName'
                          )}`}
                          error={!!error}
                          helperText={error ? error?.message : ' '}
                          {...field}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" align="center">
                          {t('dashboard.pages.createAgent.contactInfo.label')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Controller
                              name={`companyTitle.text`}
                              rules={{
                                required: t(
                                  'validation.fieldRequired'
                                ) as unknown as string,
                              }}
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  label={`${t(
                                    'dashboard.pages.createAgent.company.fields.companyTitle'
                                  )}*`}
                                  size="small"
                                  error={!!error}
                                  helperText={error ? error?.message : ' '}
                                  {...field}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Controller
                              name={`contactInfo.email`}
                              control={control}
                              rules={{
                                required: t(
                                  'validation.fieldRequired'
                                ) as unknown as string,
                              }}
                              render={({ field, fieldState: { error } }) => (
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  label={`${t(
                                    'dashboard.pages.createAgent.contactInfo.fields.email'
                                  )}*`}
                                  size="small"
                                  {...field}
                                  error={!!error}
                                  helperText={error ? error?.message : ' '}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Controller
                              rules={{
                                required: t(
                                  'validation.fieldRequired'
                                ) as unknown as string,
                              }}
                              name="description"
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  multiline
                                  minRows={3}
                                  label={`${t(
                                    'dashboard.pages.createAgent.company.fields.description'
                                  )}*`}
                                  size="small"
                                  error={!!error}
                                  helperText={error ? error?.message : ' '}
                                  {...field}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Controller
                              rules={{
                                required: t(
                                  'validation.fieldRequired'
                                ) as unknown as string,
                              }}
                              name="comments"
                              control={control}
                              render={({ field, fieldState: { error } }) => (
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  multiline
                                  minRows={3}
                                  label={`${t(
                                    'dashboard.pages.createAgent.company.fields.comments'
                                  )}*`}
                                  size="small"
                                  error={!!error}
                                  helperText={error ? error?.message : ' '}
                                  {...field}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" align="center">
                          {t(
                            'dashboard.pages.createAgent.contactInfo.fields.address.label'
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name={`address.city`}
                          control={control}
                          rules={{
                            required: t(
                              'validation.fieldRequired'
                            ) as unknown as string,
                          }}
                          render={({
                            field: { ref, ...rest },
                            fieldState: { error },
                          }) => (
                            <TextField
                              fullWidth
                              variant="outlined"
                              label={`${t(
                                'dashboard.pages.createAgent.contactInfo.fields.address.city'
                              )}*`}
                              size="small"
                              error={!!error}
                              helperText={error ? error?.message : ' '}
                              {...rest}
                              inputRef={ref}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name={`address.area`}
                          control={control}
                          rules={{
                            required: t(
                              'validation.fieldRequired'
                            ) as unknown as string,
                          }}
                          render={({
                            field: { ref, ...rest },
                            fieldState: { error },
                          }) => (
                            <TextField
                              fullWidth
                              variant="outlined"
                              label={`${t(
                                'dashboard.pages.createAgent.contactInfo.fields.address.area'
                              )}*`}
                              size="small"
                              error={!!error}
                              helperText={error ? error?.message : ' '}
                              {...rest}
                              inputRef={ref}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name={`address.street`}
                          control={control}
                          rules={{
                            required: t(
                              'validation.fieldRequired'
                            ) as unknown as string,
                          }}
                          render={({
                            field: { ref, ...rest },
                            fieldState: { error },
                          }) => (
                            <TextField
                              fullWidth
                              variant="outlined"
                              label={`${t(
                                'dashboard.pages.createAgent.contactInfo.fields.address.street'
                              )}*`}
                              size="small"
                              {...rest}
                              inputRef={ref}
                              error={!!error}
                              helperText={error ? error?.message : ' '}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name={`address.streetNumber`}
                          control={control}
                          rules={{
                            required: t(
                              'validation.fieldRequired'
                            ) as unknown as string,
                          }}
                          render={({
                            field: { ref, ...rest },
                            fieldState: { error },
                          }) => (
                            <TextField
                              fullWidth
                              variant="outlined"
                              label={`${t(
                                'dashboard.pages.createAgent.contactInfo.fields.address.streetNumber'
                              )}*`}
                              size="small"
                              {...rest}
                              inputRef={ref}
                              error={!!error}
                              helperText={error ? error?.message : ' '}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name={`address.postalCode`}
                          control={control}
                          rules={{
                            required: t(
                              'validation.fieldRequired'
                            ) as unknown as string,
                          }}
                          render={({
                            field: { ref, ...rest },
                            fieldState: { error },
                          }) => (
                            <TextField
                              fullWidth
                              variant="outlined"
                              label={`${t(
                                'dashboard.pages.createAgent.contactInfo.fields.address.postalCode'
                              )}*`}
                              size="small"
                              {...rest}
                              inputRef={ref}
                              error={!!error}
                              helperText={error ? error?.message : ' '}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Grid item container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="estateAgentSystem"
                          control={control}
                          rules={{
                            required:
                              IS_IN_EDIT_MODE && !KeycloakService.isAdmin()
                                ? false
                                : (t(
                                    'validation.fieldRequired'
                                  ) as unknown as string),
                          }}
                          render={({
                            field: { ref, ...rest },
                            fieldState: { error },
                          }) => (
                            <TextField
                              fullWidth
                              select
                              disabled={
                                IS_IN_EDIT_MODE &&
                                (!KeycloakService.isAdmin() ||
                                  watchedUserStatus === AgentStatusEnum.Active)
                              }
                              label={` ${t(
                                'dashboard.pages.createAgent.company.fields.crm'
                              )} ${
                                IS_IN_EDIT_MODE && !KeycloakService.isAdmin()
                                  ? ''
                                  : '*'
                              }`}
                              {...rest}
                              inputRef={ref}
                              size="small"
                              defaultValue=""
                              error={!!error}
                              helperText={error ? error?.message : ' '}
                              SelectProps={{
                                MenuProps: {
                                  sx: {
                                    '& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected':
                                      {
                                        bgcolor: 'primary.main',
                                      },
                                  },
                                },
                              }}
                            >
                              <MenuItem disabled value={''}>
                                {t('signUpPage.steps.step2.CRMoptions')}
                              </MenuItem>
                              {SOURCE_OPTIONS.map((item) => (
                                <MenuItem key={item.value} value={item.value}>
                                  {t(item.label)}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>
                      <External
                        control={control}
                        getValues={getValues}
                        resetField={resetField}
                        isInEditMode={IS_IN_EDIT_MODE}
                        isActiveUser={
                          watchedUserStatus === AgentStatusEnum.Active
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" align="center">
                        {t(
                          'dashboard.pages.createAgent.contactInfo.fields.telephones'
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`contactInfo.landLineNumber`}
                        control={control}
                        rules={{
                          required: false,
                        }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            fullWidth
                            variant="outlined"
                            label={`${t(
                              'dashboard.pages.createAgent.contactInfo.fields.landLineNumber'
                            )} 1`}
                            size="small"
                            {...field}
                            error={!!error}
                            helperText={error ? error?.message : ' '}
                            InputProps={{
                              endAdornment: <LocalPhone color="primary" />,
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`contactInfo.landLineNumber2`}
                        control={control}
                        rules={{ required: false }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            fullWidth
                            variant="outlined"
                            label={`${t(
                              'dashboard.pages.createAgent.contactInfo.fields.landLineNumber'
                            )} 2`}
                            size="small"
                            {...field}
                            error={!!error}
                            helperText={error ? error?.message : ' '}
                            InputProps={{
                              endAdornment: <LocalPhone color="primary" />,
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`contactInfo.mobileNumber`}
                        control={control}
                        rules={{ required: false }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            fullWidth
                            variant="outlined"
                            label={`${t(
                              'dashboard.pages.createAgent.contactInfo.fields.mobileNumber'
                            )} 1`}
                            size="small"
                            {...field}
                            error={!!error}
                            helperText={error ? error?.message : ' '}
                            InputProps={{
                              endAdornment: <PhoneAndroid color="primary" />,
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name={`contactInfo.mobileNumber2`}
                        control={control}
                        rules={{ required: false }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            fullWidth
                            variant="outlined"
                            label={`${t(
                              'dashboard.pages.createAgent.contactInfo.fields.mobileNumber'
                            )} 2`}
                            size="small"
                            {...field}
                            error={!!error}
                            helperText={error ? error?.message : ' '}
                            InputProps={{
                              endAdornment: <PhoneAndroid color="primary" />,
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" align="center">
                    {t('dashboard.pages.createAgent.partners.label')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Partners
                        control={control}
                        setValue={setValue}
                        getValues={getValues}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2" align="center">
                        {t(
                          'dashboard.pages.createAgent.contactInfo.fields.socials'
                        )}
                      </Typography>
                    </Grid>
                    {[
                      {
                        formPath: 'socialMedia.Website.url',
                        labelPath:
                          'dashboard.pages.createAgent.contactInfo.fields.socialMediaInfo.website',
                        icon: <Language color="primary" />,
                      },
                      {
                        formPath: 'socialMedia.Facebook.url',
                        labelPath:
                          'dashboard.pages.createAgent.contactInfo.fields.socialMediaInfo.fb',
                        icon: <Facebook color="fb" />,
                      },
                      {
                        formPath: 'socialMedia.Instagram.url',
                        labelPath:
                          'dashboard.pages.createAgent.contactInfo.fields.socialMediaInfo.insta',
                        icon: <Instagram color="pinterest" />,
                      },
                      {
                        formPath: 'socialMedia.Video.url',
                        labelPath:
                          'dashboard.pages.createAgent.contactInfo.fields.socialMediaInfo.video',
                        icon: <Videocam color="primary" />,
                      },
                    ].map((item) => (
                      <Grid item xs={12} sm={4} key={item.formPath}>
                        <Controller
                          name={item.formPath as Path<AgentCreateFormFields>}
                          control={control}
                          rules={{ required: false }}
                          render={({
                            field: { ref, ...rest },
                            fieldState: { error },
                          }) => (
                            <TextField
                              fullWidth
                              variant="outlined"
                              label={`${t(item.labelPath)}`}
                              size="small"
                              {...rest}
                              inputRef={ref}
                              error={!!error}
                              helperText={error ? error?.message : ' '}
                              InputProps={{
                                endAdornment: item.icon,
                              }}
                            />
                          )}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" align="center">
                    {t('dashboard.pages.createAgent.company.label')}
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name={`companyName.text` as const}
                        control={control}
                        rules={{
                          required: t(
                            'validation.fieldRequired'
                          ) as unknown as string,
                        }}
                        render={({
                          field: { ref, ...rest },
                          fieldState: { error },
                        }) => (
                          <TextField
                            fullWidth
                            variant="outlined"
                            label={`${t(
                              'dashboard.pages.createAgent.company.fields.companyName'
                            )}*`}
                            size="small"
                            {...rest}
                            inputRef={ref}
                            error={!!error}
                            helperText={error ? error?.message : ' '}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Controller
                        name={`vatNumber`}
                        control={control}
                        rules={{
                          required: t(
                            'validation.fieldRequired'
                          ) as unknown as string,
                        }}
                        render={({
                          field: { ref, ...rest },
                          fieldState: { error },
                        }) => (
                          <TextField
                            fullWidth
                            variant="outlined"
                            label={`${t(
                              'dashboard.pages.createAgent.company.fields.vatNumber'
                            )}*`}
                            size="small"
                            {...rest}
                            inputRef={ref}
                            error={!!error}
                            helperText={error ? error?.message : ' '}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Controller
                        name={`companyCommercialId`}
                        control={control}
                        rules={{
                          required: t(
                            'validation.fieldRequired'
                          ) as unknown as string,
                        }}
                        render={({
                          field: { ref, ...rest },
                          fieldState: { error },
                        }) => (
                          <TextField
                            fullWidth
                            variant="outlined"
                            label={`${t(
                              'dashboard.pages.createAgent.company.fields.companyCommercialId'
                            )}*`}
                            size="small"
                            {...rest}
                            inputRef={ref}
                            error={!!error}
                            helperText={error ? error?.message : ' '}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" align="center">
                    {t('dashboard.pages.createAgent.images.label')}
                  </Typography>
                  <Images
                    control={control}
                    setValue={setValue}
                    getValues={getValues}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  container
                  justifyContent="center"
                  spacing={3}
                >
                  <Grid item>
                    <Button
                      disabled={
                        createAgentMutation.isLoading ||
                        updateAgentMutation.isLoading
                      }
                      fullWidth
                      type="submit"
                      variant="contained"
                      sx={{
                        px: 4,
                        py: 1,
                        maxWidth: 350,
                        minWidth: 200,
                        minHeight: 70,
                        fontSize: 24,
                        fontWeight: 500,
                      }}
                    >
                      {createAgentMutation.isLoading ||
                      updateAgentMutation.isLoading ? (
                        <CircularProgress
                          style={{
                            color: 'white',
                          }}
                          size={30}
                        />
                      ) : IS_IN_EDIT_MODE ? (
                        t('dashboard.pages.createAgent.controls.update')
                      ) : (
                        t('dashboard.pages.createAgent.controls.create')
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Box>
        </div>
      </Grow>
    </>
  )
}
